/**
 * About.jsx — Studio Bio Section
 *
 * Scroll-driven "pass-through" overlay using position: fixed.
 *
 * The tile starts hidden below the viewport. As the user scrolls through
 * the scroll-budget container, the tile rises from the bottom of the
 * screen — over the FAQ section — fills the full viewport, then exits
 * off the top. When it disappears, the Collection section is revealed,
 * giving the impression the main page was quietly scrolling underneath
 * the entire time.
 *
 * Architecture:
 *   containerRef (200vh) — scroll budget, measures progress
 *     motion.div (position: fixed, zIndex: 40) — full-screen overlay tile
 *
 * useScroll offset: ['start end', 'end start']
 *   progress = 0 → container top at viewport bottom → FAQ still visible
 *   progress = 1 → container bottom at viewport top → Collection visible
 *
 * y transform:
 *   [0]   → '100%'   below screen (FAQ visible above)
 *   [1.0] → '-100%'  above screen (Collection section visible)
 *   Continuous rise — no pause
 *
 * z-index: 40 — above all page sections, below nav (100+).
 */
import { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAboutContent } from '../services/firestore';

const DARK = '#1a1208';
const CREAM = '#F9F2E9';
const GOLD = '#9C7A3C';
const NAV_H = 72;

export default function About() {
  const containerRef = useRef(null);
  const { content } = useAboutContent();

  // Track progress from when the container enters the viewport (bottom)
  // to when it exits (top). FAQ is visible at progress=0.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  // Rise continuously from below → fill screen → exit above (no pause)
  // Extra buffer ensures tile is completely hidden below viewport on all screen sizes
  const y = useTransform(
    scrollYProgress,
    [0, 1.0],
    ['calc(100% + 200px)', '-100%'],
  );

  // Hide the overlay until the scroll container is near the viewport
  // Prevents the dark background from peeking on mobile before scrolling
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (v) => {
      if (v > 0.01 && !visible) setVisible(true);
      if (v <= 0 && visible) setVisible(false);
    });
    return unsubscribe;
  }, [scrollYProgress, visible]);

  return (
    /* 200vh scroll budget — determines how long the animation lasts */
    <div ref={containerRef} style={{ position: 'relative', height: '200vh' }}>
      {/* Fixed overlay: covers the full viewport below the nav */}
      <motion.div
        initial={{ y: 'calc(100% + 200px)' }}
        style={{
          y,
          position: 'fixed',
          top: NAV_H,
          left: 0,
          right: 0,
          bottom: 0,
          background: DARK,
          overflow: 'hidden',
          zIndex: 40,
          willChange: 'transform',
          visibility: visible ? 'visible' : 'hidden',
        }}
      >
        {/* ── Content ───────────────────────────────────────────────────── */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'clamp(24px, 4vh, 48px) clamp(24px, 6vw, 72px)',
            boxSizing: 'border-box',
            overflowY: 'auto',
          }}
        >
          {/* Full width on mobile, two-column on desktop */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 'clamp(24px, 4vw, 52px)',
              width: '100%',
              maxWidth: 920,
            }}
          >
            {/* Heading above images — full width to force wrap */}
            <h2
              style={{
                fontFamily: "'Titan One', sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(1.8rem, 4.5vw, 2.6rem)',
                color: CREAM,
                margin: '0 0 20px 0',
                width: '100%',
                textAlign: 'left',
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
                textTransform: 'uppercase',
                flexBasis: '100%',
              }}
            >
              About the Artist
            </h2>
            {/* ── Photos side by side ── */}
            <div
              style={{
                flex: '1 1 280px',
                minWidth: 'clamp(260px, 100%, 400px)',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-end',
              }}
            >
              <img
                src={content.image1Url || '/artist.JPG'}
                alt="Viri Aviña, ceramic artist"
                style={{
                  flex: 1,
                  width: 0,
                  display: 'block',
                  borderRadius: 12,
                  border: `1px solid ${GOLD}44`,
                  objectFit: 'cover',
                  aspectRatio: '3/4',
                }}
              />
              <img
                src={content.image2Url || '/artist profile 2.jpg'}
                alt="Viri Aviña working with clay"
                style={{
                  flex: 1,
                  width: 0,
                  display: 'block',
                  borderRadius: 12,
                  border: `1px solid ${GOLD}44`,
                  objectFit: 'cover',
                  aspectRatio: '3/4',
                  marginBottom: 32,
                }}
              />
            </div>

            {/* ── Text ── */}
            <div
              style={{
                flex: '1.2 1 260px',
                minWidth: 'clamp(240px, 100%, 360px)',
              }}
            >
              <h3
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  fontStyle: 'italic',
                  fontSize: 'clamp(1rem, 2vw, 1.4rem)',
                  lineHeight: 1.3,
                  color: CREAM,
                  margin: '0 0 12px',
                }}
              >
                &ldquo;{content.quote}&rdquo;
              </h3>

              <div
                style={{
                  width: 36,
                  height: 1,
                  background: GOLD,
                  opacity: 0.5,
                  marginBottom: 12,
                }}
              />

              <p
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 400,
                  fontSize: 'clamp(0.8rem, 1.2vw, 0.92rem)',
                  color: CREAM,
                  lineHeight: 1.7,
                  marginBottom: 12,
                  opacity: 0.88,
                }}
              >
                {content.bio}
              </p>

              <p
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                  color: GOLD,
                  margin: 0,
                }}
              >
                {content.tagline}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
